/**
 * 航班匹配服务
 *
 * 负责计算执行航班(operations)和草稿航班(drafts)之间的匹配关系。
 *
 * 核心设计原则：
 * 1. 左右两边保持各自的原始 STD 顺序，匹配不改变显示顺序
 * 2. 手动匹配之前的航班被"锁定"，不参与自动匹配
 * 3. 匹配关系是一个映射表，用于在表格中标记对应关系
 *
 * 时间口径：
 * - 所有"起飞时间"取值统一用 pickEditorDepartureTime（ETD 优先、回退 STD），ATD 不参与
 * - 所有"到达时间"取值统一用 pickEditorArrivalTime（ETA 优先、回退 STA），ATA 不参与
 * - 这样跟监控页保持口径一致，但不把已发生的延误带进未来计划
 */

import { pickEditorDepartureTime } from "../../../../utils/timeUtils";

// ====== 常量定义 ======
export const DIFF_TYPE = {
    NO_CHANGE: "NO_CHANGE",
    ADDED: "ADDED", // 新增：执行计划没有，编辑中计划有
    REMOVED: "REMOVED", // 航班取消：执行计划有，编辑计划没有
    CHANGED: "CHANGED", // 变更：执行计划和编辑计划都有，且存在变化
};

export const MATCH_TYPE = {
    EXACT: "EXACT", // 精确匹配：起飞机场和落地机场相同，起飞时间相差<阈值
    FUZZY: "FUZZY", // 模糊匹配：起飞机场和落地机场相同，起飞时间相差在阈值范围内
    UNMATCHED: "UNMATCHED", // 未匹配
    MANUAL: "MANUAL", // 手动匹配
};

export const MANUAL_MATCH_TYPE = {
    MANUAL: "MANUAL", // 普通手动匹配
    SPLIT: "SPLIT", // 拆分
    MERGE: "MERGE", // 合并
    OTHER: "OTHER", // 其他
};

// 可参与匹配的航班状态白名单
export const MATCHABLE_STATUSES = [
    "SCHEDULED", // 已计划
    "ACTIVE", // 进行中 (前端衍生状态)
    "DELAYED", // 延误
    "DIVERTED", // 备降
    "COMPLETED", // 已完成
    "DEPARTED", // 已起飞
];

// 需要从列表中完全过滤掉的状态（不显示）
export const EXCLUDED_STATUSES = [
    "CANCELLED", // 已取消
    "PLAN_CANCELLED", // 计划取消
    "REPLACED", // 被替换
];

// 状态为已执行的航班，不应显示差异，而是显示"忽略"
export const EXECUTED_STATUSES = ["COMPLETED", "DEPARTED", "DIVERTED", "ACTIVE"];

// ====== 工具函数 ======

/**
 * 创建航班稳定标识符（自然键）
 */
export const createFlightKey = (flight) => {
    if (!flight) return null;
    return `${flight.flight_number}_${flight.departure_airport}_${flight.arrival_airport}_${flight.std}`;
};

/**
 * 判断两个航班的机场对是否相同（出发/到达机场相同）
 */
export const isSameSegment = (flight1, flight2) => {
    return flight1.departure_airport === flight2.departure_airport && flight1.arrival_airport === flight2.arrival_airport;
};

/**
 * 计算两个航班起飞时间的差距（小时）
 *
 * 取"编辑器口径"——ETD 优先、回退 STD，ATD 不参与。
 * 这样监控里改过 ETD 的航班，匹配时也按新预计时间算距离，跟监控显示一致。
 */
export const getTimeDifferenceInHours = (flight1, flight2) => {
    try {
        const date1 = new Date(pickEditorDepartureTime(flight1));
        const date2 = new Date(pickEditorDepartureTime(flight2));
        const diffMs = Math.abs(date1 - date2);
        return diffMs / (1000 * 60 * 60);
    } catch (err) {
        console.error("计算时间差异时出错:", err);
        return Infinity;
    }
};

/**
 * 按照起飞时间排序航班，返回新数组并附加原始索引
 */
export const sortByStdWithIndex = (flights) => {
    return [...flights]
        .filter((f) => f && f.std)
        .map((f, originalIndex) => ({ ...f, _originalIndex: originalIndex }))
        .sort((a, b) => {
            try {
                return new Date(a.std) - new Date(b.std);
            } catch (err) {
                console.error("排序航班时出错:", err);
                return 0;
            }
        });
};

/**
 * 按照起飞时间排序航班
 */
export const sortByStd = (flights) => {
    return [...flights]
        .filter((f) => f && f.std)
        .sort((a, b) => {
            try {
                return new Date(a.std) - new Date(b.std);
            } catch (err) {
                console.error("排序航班时出错:", err);
                return 0;
            }
        });
};

/**
 * 按"编辑器口径"起飞时间排序（ETD 优先、回退 STD；ATD 不参与）
 * 用于执行航班排序，让排序结果跟监控页显示一致。
 */
export const sortByBestTime = (flights) => {
    return [...flights]
        .filter((f) => f && pickEditorDepartureTime(f))
        .sort((a, b) => {
            try {
                const timeA = new Date(pickEditorDepartureTime(a));
                const timeB = new Date(pickEditorDepartureTime(b));
                return timeA - timeB;
            } catch (err) {
                console.error("排序航班时出错:", err);
                return 0;
            }
        });
};

/**
 * 获取航班的时间戳（毫秒）—— 按编辑器口径取起飞时间
 */
const getFlightTimestamp = (flight) => {
    try {
        const t = pickEditorDepartureTime(flight);
        return t ? new Date(t).getTime() : Infinity;
    } catch {
        return Infinity;
    }
};

/**
 * 航班号 + 出发机场 + 到达机场。航班号为空或机场缺失时不进这一组。
 */
const sameNumberAndSegmentKey = (flight) => {
    const flightNumber = flight?.flight_number;
    if (!flightNumber) return null;
    if (!flight.departure_airport || !flight.arrival_airport) return null;
    return `${flightNumber}\0${flight.departure_airport}\0${flight.arrival_airport}`;
};

const recordNumberSegmentMatch = (result, op, draft, hoursDiff, exactMatchThreshold, matchedOpIds, matchedDraftKeys) => {
    const draftKey = createFlightKey(draft);
    matchedOpIds.add(op.id);
    matchedDraftKeys.add(draftKey);

    result.matchDetails.push({
        type: hoursDiff < exactMatchThreshold ? MATCH_TYPE.EXACT : MATCH_TYPE.FUZZY,
        operations: [op],
        drafts: [draft],
        opIds: [op.id],
        draftKeys: [draftKey],
    });

    result.matchMap.set(`op_${op.id}`, draftKey);
    result.matchMap.set(`draft_${draftKey}`, op.id);
};

/**
 * 航班号相同且航段相同：有人争时按起飞时间差从小到大一对一配；
 * 只有一班对一班时仍配（改期）。时间差只决定精确 / 模糊标签。
 */
const matchSameNumberAndSegmentGroups = (unmatchedOps, unmatchedDrafts, result, exactMatchThreshold, matchedOpIds, matchedDraftKeys) => {
    const groups = new Map();

    const addToGroup = (flight, side) => {
        const key = sameNumberAndSegmentKey(flight);
        if (!key) return;
        const group = groups.get(key) || { ops: [], drafts: [] };
        groups.set(key, {
            ops: side === "ops" ? [...group.ops, flight] : group.ops,
            drafts: side === "drafts" ? [...group.drafts, flight] : group.drafts,
        });
    };

    unmatchedOps.forEach((op) => addToGroup(op, "ops"));
    unmatchedDrafts.forEach((draft) => addToGroup(draft, "drafts"));

    groups.forEach(({ ops, drafts }) => {
        if (ops.length === 0 || drafts.length === 0) return;

        const pairs = drafts.flatMap((draft) =>
            ops.map((op) => ({
                draft,
                op,
                hoursDiff: getTimeDifferenceInHours(draft, op),
                draftTime: getFlightTimestamp(draft),
                opTime: getFlightTimestamp(op),
            })),
        );

        const rankedPairs = [...pairs].sort((a, b) => {
            if (a.hoursDiff !== b.hoursDiff) return a.hoursDiff - b.hoursDiff;
            if (a.draftTime !== b.draftTime) return a.draftTime - b.draftTime;
            return a.opTime - b.opTime;
        });

        rankedPairs.forEach((pair) => {
            const draftKey = createFlightKey(pair.draft);
            if (matchedOpIds.has(pair.op.id) || matchedDraftKeys.has(draftKey)) return;
            recordNumberSegmentMatch(result, pair.op, pair.draft, pair.hoursDiff, exactMatchThreshold, matchedOpIds, matchedDraftKeys);
        });
    });
};

// ====== 核心匹配算法 ======

/**
 * 生成匹配结果
 *
 * 新算法设计：
 * 1. 左右两边各自按 STD 排序，保持原始顺序
 * 2. 手动匹配作为"锚点"，锚点之前的航班被锁定（不参与自动匹配）
 * 3. 在未锁定的范围内进行自动匹配
 * 4. 最终输出：左侧数据（按STD顺序）、右侧数据（按STD顺序）、匹配关系映射
 *
 * @param {Array} draftFlights - 草稿航班列表
 * @param {Array} operationFlights - 执行航班列表
 * @param {Object} options - 配置选项
 * @param {Array} options.manualMatchings - 手动匹配关系列表
 * @param {Object} options.matchingSettings - 匹配设置（阈值等）
 * @param {Function} options.getFlightChanges - 获取航班变更的回调函数
 * @returns {Object} 匹配结果
 */
export const generateMatchingResult = (draftFlights, operationFlights, options = {}) => {
    const {
        manualMatchings = [],
        matchingSettings = {
            exactMatchThreshold: 24, // 精确匹配阈值（小时）
            fuzzyMatchThreshold: 120, // 模糊匹配阈值（小时）
            matchingStrategy: "sequential", // 匹配策略
        },
        getFlightChanges = () => [],
    } = options;

    // 初始化结果结构
    const result = {
        totalChanges: 0,
        added: [],
        removed: [],
        changed: [],
        unchanged: [],
        // 左侧表格数据（执行航班，按STD顺序）
        leftData: [],
        // 右侧表格数据（草稿航班，按STD顺序）
        rightData: [],
        // 匹配关系映射：opId -> draftKey 或 draftKey -> opId
        matchMap: new Map(),
        // 详细匹配信息列表
        matchDetails: [],
        // 不可匹配的航班（仅供显示）
        unmatchableFlights: [],
        // 轮换分析（可选）
        rotationAnalysis: null,
    };

    if (!Array.isArray(draftFlights) || !Array.isArray(operationFlights)) {
        console.error("生成匹配结果失败: 无效的数据格式");
        return result;
    }

    // ========== 第1步：准备数据 ==========
    // 草稿航班按 STD 排序，执行航班按最佳可用时间排序（ATD > ETD > STD）
    const sortedDrafts = sortByStd(draftFlights);
    const sortedOps = sortByBestTime(operationFlights);

    // 分离可匹配和不可匹配的执行航班
    const matchableOps = sortedOps.filter((f) => f.isMatchable !== false);
    const unmatchableOps = sortedOps.filter((f) => f.isMatchable === false);
    result.unmatchableFlights = unmatchableOps;

    // 用于追踪已匹配的航班
    const matchedOpIds = new Set();
    const matchedDraftKeys = new Set();

    // ========== 第2步：处理手动匹配 ==========
    // 手动匹配的航班直接标记为已匹配，不再参与自动匹配

    for (const matching of manualMatchings) {
        // 获取匹配中的执行航班
        const ops = matching.leftIds ? sortedOps.filter((f) => f && matching.leftIds.some((id) => String(id) === String(f.id))) : [];

        // 获取匹配中的草稿航班（通过自然键匹配）
        const drafts = matching.rightKeys ? sortedDrafts.filter((f) => f && matching.rightKeys.includes(createFlightKey(f))) : matching.rightIds ? sortedDrafts.filter((f) => f && matching.rightIds.some((id) => String(id) === String(f.id))) : [];

        // 标记为已匹配
        ops.forEach((f) => {
            if (f && f.id) {
                matchedOpIds.add(f.id);
            }
        });

        drafts.forEach((f) => {
            if (f) {
                const key = createFlightKey(f);
                if (key) {
                    matchedDraftKeys.add(key);
                }
            }
        });

        // 记录匹配详情
        if (ops.length > 0 || drafts.length > 0) {
            const matchDetail = {
                type: MATCH_TYPE.MANUAL,
                manualMatchId: matching.id,
                manualMatchType: matching.type || MANUAL_MATCH_TYPE.MANUAL,
                operations: ops,
                drafts: drafts,
                opIds: ops.map((f) => f.id),
                draftKeys: drafts.map((f) => createFlightKey(f)),
            };
            result.matchDetails.push(matchDetail);

            // 添加到映射
            ops.forEach((op) => {
                drafts.forEach((draft) => {
                    result.matchMap.set(`op_${op.id}`, createFlightKey(draft));
                    result.matchMap.set(`draft_${createFlightKey(draft)}`, op.id);
                });
            });
        }
    }

    // ========== 第3步：自动匹配 ==========
    // 修复：移除"锁定边界"逻辑。手动匹配只锁定涉及的航班本身，不再锁定之前所有航班。
    // 原逻辑问题：用户手动匹配一对航班后，之前所有航班都被锁定不参与自动匹配，导致全部显示为取消+新增。
    const { exactMatchThreshold, fuzzyMatchThreshold, matchingStrategy } = matchingSettings;

    // 获取未匹配的航班（只排除已被手动匹配占用的）
    const unmatchedOps = matchableOps.filter((f) => !matchedOpIds.has(f.id));
    const unmatchedDrafts = sortedDrafts.filter((f) => !matchedDraftKeys.has(createFlightKey(f)));

    // ========== Phase 0: 航班号相同且航段相同 ==========
    // 有人争（一边一班、另一边两班或更多也算）时，起飞更近的先配上，已配上的不再给别人。
    // 只有一班对一班时仍配，即使差几天（改期）。时间差只决定精确 / 模糊标签。
    matchSameNumberAndSegmentGroups(unmatchedOps, unmatchedDrafts, result, exactMatchThreshold, matchedOpIds, matchedDraftKeys);

    // ========== Phase 1: 剩余航班匹配 ==========
    // 本组没配上的班、以及航班号或航段不同的班，按顺序匹配 / 最优匹配去配出发到达相同、航班号可以不同的剩余班。
    const remainingOps = unmatchedOps.filter((f) => !matchedOpIds.has(f.id));
    const remainingDrafts = unmatchedDrafts.filter((f) => !matchedDraftKeys.has(createFlightKey(f)));

    if (matchingStrategy === "sequential") {
        // 顺序匹配策略：双指针按时间顺序依次配对
        // 时间差仅决定匹配类型（精确/模糊），不用于选择配对对象
        let rOpIdx = 0;
        let rDraftIdx = 0;

        while (rOpIdx < remainingOps.length && rDraftIdx < remainingDrafts.length) {
            const op = remainingOps[rOpIdx];
            const draft = remainingDrafts[rDraftIdx];
            const draftKey = createFlightKey(draft);

            if (isSameSegment(draft, op)) {
                const hoursDiff = getTimeDifferenceInHours(draft, op);

                if (hoursDiff < fuzzyMatchThreshold) {
                    // 匹配成功：机场对一致且时间差在阈值内
                    matchedOpIds.add(op.id);
                    matchedDraftKeys.add(draftKey);

                    const matchType = hoursDiff < exactMatchThreshold
                        ? MATCH_TYPE.EXACT
                        : MATCH_TYPE.FUZZY;

                    result.matchDetails.push({
                        type: matchType,
                        operations: [op],
                        drafts: [draft],
                        opIds: [op.id],
                        draftKeys: [draftKey],
                    });

                    result.matchMap.set(`op_${op.id}`, draftKey);
                    result.matchMap.set(`draft_${draftKey}`, op.id);

                    rOpIdx++;
                    rDraftIdx++;
                } else {
                    // 时间差超过模糊阈值 → 不匹配，时间较早者前进
                    const opTime = getFlightTimestamp(op);
                    const draftTime = getFlightTimestamp(draft);
                    if (opTime <= draftTime) {
                        rOpIdx++;
                    } else {
                        rDraftIdx++;
                    }
                }
            } else {
                // 机场对不同 → 不匹配，时间较早者前进
                const opTime = getFlightTimestamp(op);
                const draftTime = getFlightTimestamp(draft);
                if (opTime <= draftTime) {
                    rOpIdx++;
                } else {
                    rDraftIdx++;
                }
            }
        }
    } else {
        // 最优匹配策略：为每个草稿航班寻找时间差最小的执行航班
        for (const draft of remainingDrafts) {
            const draftKey = createFlightKey(draft);
            if (matchedDraftKeys.has(draftKey)) continue;

            let bestMatch = null;
            let bestMatchType = null;
            let bestHoursDiff = Infinity;

            for (const op of remainingOps) {
                if (matchedOpIds.has(op.id)) continue;
                if (!isSameSegment(draft, op)) continue;

                const hoursDiff = getTimeDifferenceInHours(draft, op);

                if (hoursDiff < bestHoursDiff) {
                    bestHoursDiff = hoursDiff;

                    if (hoursDiff < exactMatchThreshold) {
                        bestMatch = op;
                        bestMatchType = MATCH_TYPE.EXACT;
                    } else if (hoursDiff < fuzzyMatchThreshold) {
                        bestMatch = op;
                        bestMatchType = MATCH_TYPE.FUZZY;
                    } else {
                        bestMatch = null;
                        bestMatchType = null;
                    }
                }
            }

            if (bestMatch && bestMatchType) {
                matchedOpIds.add(bestMatch.id);
                matchedDraftKeys.add(draftKey);

                result.matchDetails.push({
                    type: bestMatchType,
                    operations: [bestMatch],
                    drafts: [draft],
                    opIds: [bestMatch.id],
                    draftKeys: [draftKey],
                });

                result.matchMap.set(`op_${bestMatch.id}`, draftKey);
                result.matchMap.set(`draft_${draftKey}`, bestMatch.id);
            }
        }
    }

    // ========== 第4步：处理剩余未匹配航班 ==========
    // 未匹配的执行航班
    for (const op of matchableOps) {
        if (!matchedOpIds.has(op.id)) {
            matchedOpIds.add(op.id);
            result.matchDetails.push({
                type: MATCH_TYPE.UNMATCHED,
                operations: [op],
                drafts: [],
                opIds: [op.id],
                draftKeys: [],
            });
        }
    }

    // 未匹配的草稿航班
    for (const draft of sortedDrafts) {
        const key = createFlightKey(draft);
        if (!matchedDraftKeys.has(key)) {
            matchedDraftKeys.add(key);
            result.matchDetails.push({
                type: MATCH_TYPE.UNMATCHED,
                operations: [],
                drafts: [draft],
                opIds: [],
                draftKeys: [key],
            });
        }
    }

    // ========== 第5步：生成对齐的表格数据（类似代码diff）==========
    // 核心思路：用双指针遍历排序后的左右航班列表
    // - 如果当前左右航班匹配 -> 放在同一行
    // - 如果左边航班未匹配（取消）-> 左边显示航班，右边显示空
    // - 如果右边航班未匹配（新增）-> 左边显示空，右边显示航班

    // 创建查找映射
    const opIdToMatchDetail = new Map();
    const draftKeyToMatchDetail = new Map();

    result.matchDetails.forEach((detail, idx) => {
        detail.opIds.forEach((id) => opIdToMatchDetail.set(id, { detail, idx }));
        detail.draftKeys.forEach((key) => draftKeyToMatchDetail.set(key, { detail, idx }));
    });

    // 创建 opId -> matchedDraftKey 和 draftKey -> matchedOpId 的直接映射
    const opToMatchedDraft = new Map();
    const draftToMatchedOp = new Map();

    result.matchDetails.forEach((detail) => {
        if (detail.operations.length > 0 && detail.drafts.length > 0) {
            // 有匹配关系
            const opId = detail.opIds[0];
            const draftKey = detail.draftKeys[0];
            opToMatchedDraft.set(opId, { draft: detail.drafts[0], draftKey, detail });
            draftToMatchedOp.set(draftKey, { op: detail.operations[0], opId, detail });
        }
    });

    // 用于追踪已处理的航班
    const processedOpIds = new Set();
    const processedDraftKeys = new Set();

    // 双指针遍历
    let opIdx = 0;
    let draftIdx = 0;
    let rowIndex = 0;

    while (opIdx < sortedOps.length || draftIdx < sortedDrafts.length) {

        const currentOp = opIdx < sortedOps.length ? sortedOps[opIdx] : null;
        const currentDraft = draftIdx < sortedDrafts.length ? sortedDrafts[draftIdx] : null;
        const currentDraftKey = currentDraft ? createFlightKey(currentDraft) : null;

        // 检查当前执行航班是否有匹配
        const opMatchInfo = currentOp ? opToMatchedDraft.get(currentOp.id) : null;
        // 检查当前草稿航班是否有匹配
        const draftMatchInfo = currentDraftKey ? draftToMatchedOp.get(currentDraftKey) : null;

        let leftFlight = null;
        let rightFlight = null;
        let matchDetail = null;
        let advanceOp = false;
        let advanceDraft = false;

        if (currentOp && currentDraft) {
            // 两边都有航班
            if (opMatchInfo && opMatchInfo.draftKey === currentDraftKey) {
                // 当前左右航班正好匹配 -> 放在同一行
                leftFlight = currentOp;
                rightFlight = currentDraft;
                matchDetail = opMatchInfo.detail;
                advanceOp = true;
                advanceDraft = true;
            } else if (opMatchInfo && !processedDraftKeys.has(opMatchInfo.draftKey)) {
                // 左边航班（currentOp）有匹配，但匹配的草稿尚未被指针扫到。
                //
                // 【交叉等待处理 Cross-Wait Handling】
                // 当前草稿也有自己待出现的匹配 Op → 双方各自等待对方的匹配伙伴。
                // 按时间顺序输出当前航班，确保左右两侧始终保持各自的原始时间排序。
                // 匹配的航班对可能出现在不同行，但顺序不会被打乱。
                if (draftMatchInfo && !processedOpIds.has(draftMatchInfo.opId)) {
                    // 交叉等待：按时间先后决定输出哪一侧
                    const opTime = getFlightTimestamp(currentOp);
                    const draftTime = getFlightTimestamp(currentDraft);
                    if (opTime <= draftTime) {
                        // 执行航班时间更早，先输出左侧
                        leftFlight = currentOp;
                        rightFlight = null;
                        matchDetail = opIdToMatchDetail.get(currentOp.id)?.detail;
                        advanceOp = true;
                    } else {
                        // 草稿航班时间更早，先输出右侧
                        leftFlight = null;
                        rightFlight = currentDraft;
                        matchDetail = draftKeyToMatchDetail.get(currentDraftKey)?.detail;
                        advanceDraft = true;
                    }
                } else {
                    // 正常情况：当前草稿确实无配对，视为新增
                    leftFlight = null;
                    rightFlight = currentDraft;
                    matchDetail = draftKeyToMatchDetail.get(currentDraftKey)?.detail;
                    advanceDraft = true;
                }
            } else if (draftMatchInfo && !processedOpIds.has(draftMatchInfo.opId)) {
                // 右边航班有匹配，但匹配的执行航班还未出现 -> 等待左边追上
                // 当前左边航班应该是取消的
                leftFlight = currentOp;
                rightFlight = null;
                matchDetail = opIdToMatchDetail.get(currentOp.id)?.detail;
                advanceOp = true;
            } else if (!opMatchInfo && !draftMatchInfo) {
                // 两边都没有匹配 -> 按时间顺序决定先显示谁
                const opTime = getFlightTimestamp(currentOp);
                const draftTime = getFlightTimestamp(currentDraft);

                if (opTime <= draftTime) {
                    // 左边航班时间更早或相同 -> 先显示左边（取消）
                    leftFlight = currentOp;
                    rightFlight = null;
                    matchDetail = opIdToMatchDetail.get(currentOp.id)?.detail;
                    advanceOp = true;
                } else {
                    // 右边航班时间更早 -> 先显示右边（新增）
                    leftFlight = null;
                    rightFlight = currentDraft;
                    matchDetail = draftKeyToMatchDetail.get(currentDraftKey)?.detail;
                    advanceDraft = true;
                }
            } else {
                // 匹配的航班已经被处理过了 -> 按时间顺序处理当前航班
                const opTime = getFlightTimestamp(currentOp);
                const draftTime = getFlightTimestamp(currentDraft);

                if (opTime <= draftTime && !processedOpIds.has(currentOp.id)) {
                    leftFlight = currentOp;
                    rightFlight = null;
                    matchDetail = opIdToMatchDetail.get(currentOp.id)?.detail;
                    advanceOp = true;
                } else if (!processedDraftKeys.has(currentDraftKey)) {
                    leftFlight = null;
                    rightFlight = currentDraft;
                    matchDetail = draftKeyToMatchDetail.get(currentDraftKey)?.detail;
                    advanceDraft = true;
                } else {
                    // 都处理过了，跳过
                    advanceOp = !processedOpIds.has(currentOp.id) ? false : true;
                    advanceDraft = !processedDraftKeys.has(currentDraftKey) ? false : true;
                    if (!advanceOp && !advanceDraft) {
                        advanceOp = true;
                        advanceDraft = true;
                    }
                }
            }
        } else if (currentOp && !currentDraft) {
            // 只剩左边有航班 -> 全是取消
            leftFlight = currentOp;
            rightFlight = null;
            matchDetail = opIdToMatchDetail.get(currentOp.id)?.detail;
            advanceOp = true;
        } else if (!currentOp && currentDraft) {
            // 只剩右边有航班 -> 全是新增
            leftFlight = null;
            rightFlight = currentDraft;
            matchDetail = draftKeyToMatchDetail.get(currentDraftKey)?.detail;
            advanceDraft = true;
        } else {
            // 都没有了，结束
            break;
        }

        // 标记已处理
        if (leftFlight) processedOpIds.add(leftFlight.id);
        if (rightFlight) processedDraftKeys.add(createFlightKey(rightFlight));

        // 计算差异类型
        let diffType;
        let changes = [];

        if (leftFlight && rightFlight) {
            // 有匹配
            if (leftFlight.isMatchable === false) {
                diffType = "DISPLAY_ONLY";
            } else if (EXECUTED_STATUSES.includes(leftFlight.status)) {
                diffType = "EXECUTED_IGNORED";
            } else {
                changes = getFlightChanges(rightFlight, leftFlight);
                if (matchDetail?.type === MATCH_TYPE.MANUAL) {
                    diffType = DIFF_TYPE.CHANGED;
                } else {
                    diffType = changes.length > 0 ? DIFF_TYPE.CHANGED : DIFF_TYPE.NO_CHANGE;
                }
            }
        } else if (leftFlight && !rightFlight) {
            // 取消
            if (leftFlight.isMatchable === false) {
                diffType = "DISPLAY_ONLY";
            } else if (EXECUTED_STATUSES.includes(leftFlight.status)) {
                diffType = "EXECUTED_IGNORED";
            } else {
                diffType = DIFF_TYPE.REMOVED;
            }
        } else if (!leftFlight && rightFlight) {
            // 新增
            diffType = DIFF_TYPE.ADDED;
        }

        // 生成行数据
        result.leftData.push({
            key: `left_${rowIndex}`,
            rowKey: rowIndex,
            subRowKey: 0,
            flight: leftFlight,
            matchedFlight: rightFlight,
            diffType,
            changes,
            isEmpty: !leftFlight,
            matchType: matchDetail?.type || MATCH_TYPE.UNMATCHED,
            isGroupStart: true,
            isGroupEnd: true,
            totalInGroup: 1,
            groupId: rowIndex,
            isManualMatch: matchDetail?.type === MATCH_TYPE.MANUAL,
            isLocked: matchDetail?.isLocked || false,
            isDisplayOnly: leftFlight?.isMatchable === false,
        });

        result.rightData.push({
            key: `right_${rowIndex}`,
            rowKey: rowIndex,
            subRowKey: 0,
            flight: rightFlight,
            matchedFlight: leftFlight,
            diffType,
            changes,
            isEmpty: !rightFlight,
            matchType: matchDetail?.type || MATCH_TYPE.UNMATCHED,
            isGroupStart: true,
            isGroupEnd: true,
            totalInGroup: 1,
            groupId: rowIndex,
            isManualMatch: matchDetail?.type === MATCH_TYPE.MANUAL,
            isLocked: matchDetail?.isLocked || false,
        });

        // 收集统计信息
        if (diffType !== "DISPLAY_ONLY" && diffType !== "EXECUTED_IGNORED") {
            if (diffType === DIFF_TYPE.REMOVED && leftFlight) {
                result.removed.push({
                    operation: leftFlight,
                    draft: null,
                    matchType: matchDetail?.type || MATCH_TYPE.UNMATCHED,
                });
            } else if (diffType === DIFF_TYPE.ADDED && rightFlight) {
                result.added.push({
                    operation: null,
                    draft: rightFlight,
                    matchType: MATCH_TYPE.UNMATCHED,
                });
            } else if (diffType === DIFF_TYPE.CHANGED && leftFlight && rightFlight) {
                result.changed.push({
                    operation: leftFlight,
                    draft: rightFlight,
                    matchType: matchDetail?.type || MATCH_TYPE.EXACT,
                    changes,
                });
            } else if (diffType === DIFF_TYPE.NO_CHANGE && leftFlight && rightFlight) {
                result.unchanged.push({
                    operation: leftFlight,
                    draft: rightFlight,
                    matchType: matchDetail?.type || MATCH_TYPE.EXACT,
                });
            }
        }

        // 移动指针
        if (advanceOp) opIdx++;
        if (advanceDraft) draftIdx++;
        rowIndex++;
    }

    // 计算总变更数
    result.totalChanges = result.added.length + result.removed.length + result.changed.length;

    return result;
};

/**
 * 获取手动匹配中最早航班的时间戳
 */
function getManualMatchMinTime(matching, sortedOps, sortedDrafts) {
    const opTimes = matching.leftIds ? sortedOps.filter((f) => f && matching.leftIds.some((id) => String(id) === String(f.id))).map((f) => getFlightTimestamp(f)) : [];

    const draftTimes = matching.rightKeys ? sortedDrafts.filter((f) => f && matching.rightKeys.includes(createFlightKey(f))).map((f) => getFlightTimestamp(f)) : matching.rightIds ? sortedDrafts.filter((f) => f && matching.rightIds.some((id) => String(id) === String(f.id))).map((f) => getFlightTimestamp(f)) : [];

    const allTimes = [...opTimes, ...draftTimes].filter((t) => t !== Infinity && !isNaN(t));
    return allTimes.length > 0 ? Math.min(...allTimes) : Infinity;
}

/**
 * 获取航班变更详情（默认实现，供未传入自定义比较器的场景使用）
 *
 * 时间字段比较口径：
 * - draftFlight（编辑中草案）侧：草案没有 etd/eta 概念，仍取 std/sta
 * - operationFlight（执行航班）侧：按编辑器口径取值——std 字段实际比较 etd ?? std、sta 字段实际比较 eta ?? sta
 * - 这样监控里改过 ETD / ETA 的航班，对比时能正确识别为"变更"，不会再出现"看着不一样、判定相同"
 */
export const getFlightChangesDefault = (draftFlight, operationFlight) => {
    const changes = [];
    const fieldsToCompare = [
        { key: "flight_number", label: "航班号" },
        { key: "departure_airport", label: "出发机场" },
        { key: "arrival_airport", label: "到达机场" },
        { key: "std", label: "计划起飞时间", opField: "etd", opFallback: "std" },
        { key: "sta", label: "计划到达时间", opField: "eta", opFallback: "sta" },
        { key: "stc_type", label: "航班性质" },
        { key: "flight_date", label: "航班日期" },
    ];

    fieldsToCompare.forEach(({ key, label, opField, opFallback }) => {
        // 日期字段
        if (key === "flight_date") {
            const getDateOnly = (dateStr) => {
                if (!dateStr) return "";
                try {
                    return dateStr.split("T")[0] || dateStr.split(" ")[0] || dateStr;
                } catch (e) {
                    console.error("提取日期时出错:", e);
                    return dateStr;
                }
            };

            const oldDateOnly = getDateOnly(operationFlight[key]);
            const newDateOnly = getDateOnly(draftFlight[key]);

            if (oldDateOnly !== newDateOnly) {
                changes.push({
                    field: key,
                    fieldLabel: label,
                    oldValue: operationFlight[key],
                    newValue: draftFlight[key],
                    oldDateOnly,
                    newDateOnly,
                    isDateField: true,
                });
            }
        }
        // 时间字段
        else if (key.includes("time") || key.includes("std") || key.includes("sta")) {
            // 监控侧按编辑器口径取（ETD/ETA 优先，回退 STD/STA）；草案侧仍取 std/sta
            const oldRaw = opField ? operationFlight[opField] ?? operationFlight[opFallback] : operationFlight[key];
            const newRaw = draftFlight[key];

            const oldFormatted = formatTimeValue(oldRaw);
            const newFormatted = formatTimeValue(newRaw);

            if (oldFormatted !== newFormatted) {
                changes.push({
                    field: key,
                    fieldLabel: label,
                    oldValue: oldRaw,
                    newValue: newRaw,
                });
            }
        }
        // 其他字段
        else if (draftFlight[key] !== operationFlight[key]) {
            changes.push({
                field: key,
                fieldLabel: label,
                oldValue: operationFlight[key],
                newValue: draftFlight[key],
            });
        }
    });

    return changes;
};

/**
 * 格式化分钟为小时和分钟
 */
export const formatMinutes = (minutes) => {
    if (minutes === undefined || minutes === null) return "";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
};

/**
 * 计算时间差异并格式化为增加或减少的时间
 */
export const calculateTimeDifference = (oldValue, newValue) => {
    if (!oldValue || !newValue) return null;

    try {
        const oldDate = new Date(oldValue);
        const newDate = new Date(newValue);

        if (isNaN(oldDate.getTime()) || isNaN(newDate.getTime())) {
            return null;
        }

        const diffMs = newDate - oldDate;
        const diffMinutes = Math.floor(Math.abs(diffMs) / (1000 * 60));
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;
        const direction = diffMs >= 0 ? "+" : "-";

        if (hours > 0 && minutes > 0) {
            return `${direction}${hours}h ${minutes}m`;
        } else if (hours > 0) {
            return `${direction}${hours}h`;
        } else {
            return `${direction}${minutes}m`;
        }
    } catch (e) {
        console.error("计算时间差异时出错:", e);
        return null;
    }
};

/**
 * 格式化时间值显示
 */
export const formatTimeValue = (timeValue) => {
    if (!timeValue) return "";

    if (typeof timeValue === "string") {
        if (timeValue.includes("T")) {
            try {
                return timeValue.split("T")[1].substring(0, 5);
            } catch (e) {
                console.error("时间格式化错误:", e);
                return timeValue;
            }
        }

        if (timeValue.includes(":")) {
            return timeValue.substring(0, 5);
        }
    }

    return timeValue;
};

/**
 * 格式化日期显示
 */
export const formatDateValue = (dateValue) => {
    if (!dateValue) return "";

    if (typeof dateValue === "string") {
        if (dateValue.includes("-")) {
            return dateValue.split("T")[0].split(" ")[0];
        }
    }

    return dateValue;
};

/**
 * 提取轮换路径信息（用于分析）
 *
 * std / sta 字段按编辑器口径取值——监控侧用 ETD/ETA 优先，草案侧自然回退到 STD/STA。
 * 这样轮换路径分析跟监控页和对比页保持口径一致。
 */
export const extractRotationPath = (flights) => {
    if (!flights || flights.length === 0) return [];

    return flights
        .map((flight) => {
            if (!flight) return null;
            return {
                date: flight.flight_date || "",
                from: flight.departure_airport || "",
                to: flight.arrival_airport || "",
                flightNumber: flight.flight_number || "",
                std: pickEditorDepartureTime(flight) || "",
                sta: (flight.eta ?? flight.sta) || "",
            };
        })
        .filter(Boolean);
};

export default {
    DIFF_TYPE,
    MATCH_TYPE,
    MANUAL_MATCH_TYPE,
    MATCHABLE_STATUSES,
    EXCLUDED_STATUSES,
    EXECUTED_STATUSES,
    createFlightKey,
    isSameSegment,
    getTimeDifferenceInHours,
    sortByStd,
    sortByStdWithIndex,
    sortByBestTime,
    generateMatchingResult,
    getFlightChangesDefault,
    formatMinutes,
    calculateTimeDifference,
    formatTimeValue,
    formatDateValue,
    extractRotationPath,
};
